/**
 * Authenticated Layout Route
 *
 * Layout route that wraps all protected routes under /$locale/_authenticated/*.
 * Performs authentication check in beforeLoad and redirects to login if needed.
 *
 * Protected routes:
 * - /$locale/_authenticated/instructor/* - Instructor dashboard
 * - /$locale/_authenticated/admin/* - Admin panel
 * - /$locale/_authenticated/student/* - Student portal
 *
 * @example
 * // Route structure
 * routes/$locale/
 * ├── _authenticated.tsx          # This file (layout)
 * └── _authenticated/
 *     ├── instructor/             # Protected
 *     ├── admin/                  # Protected
 *     └── student/                # Protected
 *
 * @see https://tanstack.com/router/latest/docs/framework/react/guide/authenticated-routes
 * @added DOC-0074 (2025-12-19)
 */

import {
  createFileRoute,
  redirect,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import type { RouterContext } from '@shared/config/router-context';
import { AuthLoading, useAuth } from '@features/auth';
import { AuthenticatedShell } from '@widgets/authenticated-layout';

export function getAuthenticatedReturnPath(pathname: string, searchStr: string): string {
  return searchStr.length > 0 ? `${pathname}${searchStr}` : pathname;
}

export const Route = createFileRoute('/$locale/_authenticated')({
  /**
   * Authentication guard
   *
   * Runs before the route loads. Checks if user is authenticated
   * and redirects to login if not.
   *
   * The redirect includes the original URL as a query param so
   * the user can be redirected back after login.
   */
  beforeLoad: ({ context, location, params }) => {
    const { auth } = context as RouterContext;

    // Safety: if we are already on the login page, never redirect again.
    // This prevents recursive `?redirect=` growth in case this pathless route is matched unexpectedly.
    if (location.pathname === `/${params.locale}/login`) {
      return;
    }

    // If auth is still loading, we can't determine auth state yet
    // The component will handle showing loading state
    if (auth.isLoading) {
      return;
    }

    // Redirect to login if not authenticated
    if (!auth.isAuthenticated) {
      // TanStack Router requires throwing redirect() result
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/login',
        params: { locale: params.locale },
        search: {
          redirect: getAuthenticatedReturnPath(location.pathname, location.searchStr),
        },
      });
    }
  },
  component: AuthenticatedLayout,
});

/**
 * Authenticated Layout Component
 *
 * Wrapper for all authenticated routes. Shows loading state while
 * auth is being determined, then renders child routes.
 *
 * Can be extended to include:
 * - Common navigation
 * - Sidebar
 * - User menu
 */
function AuthenticatedLayout() {
  const { locale } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    if (location.pathname === `/${locale}/login`) return;

    void navigate({
      to: '/$locale/login',
      params: { locale },
      search: { redirect: getAuthenticatedReturnPath(location.pathname, location.searchStr) },
      replace: true,
    });
  }, [isAuthenticated, isLoading, locale, location.pathname, location.searchStr, navigate]);

  // Show loading while auth state is being determined
  if (isLoading) {
    return <AuthLoading />;
  }

  // Extra safety check - should not reach here if beforeLoad works correctly
  // But provides fallback in case of race conditions
  if (!isAuthenticated) {
    return <AuthLoading message="Redirecting to login..." />;
  }

  return (
    <AuthenticatedShell locale={locale}>
      <Outlet />
    </AuthenticatedShell>
  );
}
